import { useEffect, useMemo, useState } from 'react';
import Confetti from 'react-confetti';
import { Check } from 'lucide-react';

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button, Text } from '@/components/flup';

/**
 * The moment a venue goes live.
 *
 * One success signal, not three: the tick carries it, and the copy says what
 * actually changed rather than restating that a payment succeeded. The old
 * version painted a yellow-to-pink wash over the card, which is fine on a white
 * surface and turns to olive sludge on a dark one — everything here now comes
 * from the theme tokens instead.
 */

interface PaymentSuccessAnimationProps {
  show: boolean;
  /** Continue — and the dialog's own dismiss. */
  action: () => void;
  venueName?: string;
  /** e.g. "Zero commission" or "5% per booking". */
  planLabel?: string;
}

const CONFETTI_MS = 6000;

const PaymentSuccessAnimation = ({
  show,
  action,
  venueName,
  planLabel,
}: PaymentSuccessAnimationProps) => {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [showConfetti, setShowConfetti] = useState(false);

  // A full-screen burst of confetti is exactly what this setting is for.
  const reducedMotion = useMemo(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  useEffect(() => {
    if (!show || reducedMotion) return;
    setShowConfetti(true);
    const timer = setTimeout(() => setShowConfetti(false), CONFETTI_MS);
    return () => clearTimeout(timer);
  }, [show, reducedMotion]);

  useEffect(() => {
    const handleResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {show && showConfetti ? (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={250}
          className="pointer-events-none"
        />
      ) : null}

      <Dialog open={show} onOpenChange={(isOpen) => (!isOpen ? action() : undefined)}>
        <DialogContent className="max-w-sm text-center">
          <div className="flex flex-col items-center pt-2">
            <span className="grid place-items-center w-16 h-16 rounded-pill bg-flup-green-soft mb-5">
              <Check className="w-8 h-8 text-flup-green" strokeWidth={3} aria-hidden />
            </span>

            <DialogTitle asChild>
              <Text variant="h1" as="h2">
                You’re live!
              </Text>
            </DialogTitle>

            <Text variant="body" tone="secondary" as="p" className="mt-2">
              {venueName ? (
                <>
                  <span className="font-semibold text-flup-ink">{venueName}</span> is now listed and
                  taking bookings.
                </>
              ) : (
                'Your venue is now listed and taking bookings.'
              )}
            </Text>

            {planLabel ? (
              <span className="mt-4 rounded-pill bg-flup-surface-alt px-3 py-1">
                <Text variant="caption" tone="secondary" as="span">
                  {planLabel}
                </Text>
              </span>
            ) : null}

            <Button fullWidth className="mt-6" onClick={action}>
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PaymentSuccessAnimation;
